if (POLYFILL_ENABLED) {
  (
    new Promise(function () {
    }) &&
    new Map() &&
    new WeakMap()
  );
}

const promiseMap = new Map();
let globalIdDefaultPrefix = `guid-${Math.floor(Math.random() * 2147483647)}`;
let globalIdIndex = Math.floor(Math.random() * 10000);

export function guid(opt_prefix = '') {
  return `${opt_prefix || globalIdDefaultPrefix}-${globalIdIndex++}`
}

function consoleByMethodName(methodName, args) {
  if (LOGGING_ENABLED && console) {
    if (console[methodName]) {
      console[methodName].apply(console, args);
    }
    else if (methodName !== 'log') {
      consoleByMethodName('log', args);
    }
  }
}

export function scope(fn) {
  fn();
}

export function log(...args) {
  consoleByMethodName('log', args);
}

export function info(...args) {
  consoleByMethodName('info', args);
}

export function error(...args) {
  consoleByMethodName('error', args);
}

export function warn(...args) {
  consoleByMethodName('warn', args);
}

export function assert(...args) {
  consoleByMethodName('assert', args);
}

/**
 * @param namesOrPromises {...string|!Promise|!Array.<string|!Promise>}
 * @returns Promise
 **/
export function when(...namesOrPromises) {
  const promises = [];
  for(let nameOrPromise of namesOrPromises) {
    let promise;
    if (nameOrPromise instanceof Promise) {
      promise = nameOrPromise;
    }
    else if (typeof nameOrPromise === 'string' || Array.isArray(nameOrPromise)) {
      promise = ensurePromiseMapping(nameOrPromise).promise;
    }
    else {
      assert(false, 'Expected arguments to be either a promise, a string, or a list got: %O.', nameOrPromise);
      return null;
    }

    promises.push(promise);
  }

  if (promises.length === 1) {
    return promises[0];
  }

  return Promise.all(promises);
}

function ensurePromiseMapping(name) {
  if (!promiseMap.get(name)) {
    let mapping = createPromiseMapping();
    promiseMap.set(name, mapping);

    if (LOGGING_ENABLED) {
      mapping.promise.then((value) => {
        info('resolvePromiseByName', name, value);
      });
    }

    if (Array.isArray(name)) {
      when.apply(null, name)
        .then(resolvePromiseByName.bind(null, name))
        .catch(rejectPromiseByName.bind(null, name));
    }
  }

  return promiseMap.get(name);
}

function createPromiseMapping() {
  let mapping = {};
  mapping.promise = new Promise(function (resolve, reject) {
    mapping.resolve = resolve;
    mapping.reject = reject;
  });

  return mapping;
}
/**
 * @param namesOrPromise {string}
 * @param opt_value {*=} The value to send to the resolved promise
 * @returns Promise
 **/
export function resolvePromiseByName(name, opt_value) {
  let mapping = ensurePromiseMapping(name);
  return mapping.resolve(opt_value);
}

export function resolve(dataOrName, opt_value) {
  if (typeof dataOrName === 'string') {
    return resolvePromiseByName(dataOrName, opt_value);
  }
  else {
    const nameList = [];
    for(let name in dataOrName) {
      nameList.push(name);
      resolvePromiseByName(dataOrName, dataOrName[name])
    }

    return when(nameList);
  }
}

/**
 * @param namesOrPromise {string}
 * @param opt_reason {*=} The error or message to send to the resolved promise
 * @returns Promise
 **/
export function rejectPromiseByName(name, opt_reason) {
  let mapping = ensurePromiseMapping(name);
  return mapping.reject(opt_reason);
}

export function reject(dataOrName, opt_value) {
  if (typeof dataOrName === 'string') {
    return rejectPromiseByName(dataOrName, opt_value);
  }
  else {
    const nameList = [];
    for(let name in dataOrName) {
      nameList.push(name);
      rejectPromiseByName(dataOrName, dataOrName[name])
    }

    return when(nameList);
  }
}

export function listenTo(subject, type, callback, opt_context) {
  if (opt_context) {
    callback = callback.bind(opt_context);
  }

  const onMethodName = `on${type}`;
  if (typeof subject.addEventListener === 'function') {
    subject.addEventListener(type, callback, false)
  }
  else if (typeof subject.attachEvent === 'function') {
    subject.attachEvent(onMethodName, onReadyStateChange);
  }
  if (typeof subject.on === 'function') {
    subject.on(type, callback);
  }
  else if (onMethodName in subject) {
    if (subject[onMethodName]) {
      subject[onMethodName] = function (originalHandler, ...args) {
        originalHandler.apply(this, args);
        return hanlder.apply(this, args);
      }.bind(subject, subject[onMethodName])
    }
  }
}

export function stopListening(subject, type, callback) {
  const onMethodName = `on${type}`;
  if (typeof subject.removeEventListener === 'function') {
    subject.removeEventListener(type, callback, false)
  }
  else if (typeof subject.detachEvent === 'function') {
    subject.detachEvent(onMethodName, onReadyStateChange);
  }
  if (typeof subject.off === 'function') {
    subject.off(type, callback);
  }
  else if (onMethodName in subject) {
    if (subject[onMethodName]) {
      subject[onMethodName] = null;
    }
  }
}
const fetchScriptUrl = '//cdnjs.cloudflare.com/ajax/libs/fetch/2.0.3/fetch.min.js';
const fetchCache = new Map();
export function fetch(url, opt_data) {
  if (window.fetch) {
    resolvePromiseByName('fetch', window.fetch);
  }
  else if (!fetchCache.get(fetchScriptUrl)) {
    fetchCache.set(fetchScriptUrl, true);
    addScriptTag({
      src: fetchScriptUrl,
      type: 'text/javascript',
      async: true,
      onLoad: 'evite.resolve({fetch: fetch});',
      onError: 'evite.reject("fetch");'
    });
  }

  when('fetch').then(function (fetch) {
    if (!fetchCache.get(url)) {
      fetchCache.set(url, true);
      fetch(url, opt_data)
        .then(resolvePromiseByName.bind(null, url))
        .catch(rejectPromiseByName.bind(null, url));
    }
  });

  return when(url);
}

// TODO - refactor
export function addScriptTag({ src, type, defer, async, onLoad, onError }) {
  defer = Boolean(defer);
  async = Boolean(async);
  onLoad = onLoad || '';
  onError = onError || '';

  const id = guid('script-id');
  const expandedOnLoad = `evite.resolve("${src}", document.getElementById(${id})); ${onLoad}`;
  const expandedOnError = `evite.reject("${src}"); ${onError}`;

  const isDocumentReady = document.readyState === 'interactive' || document.readyState === 'complete';
  if (isDocumentReady) {
    let scriptTag = document.createElement('script');

    scriptTag.type = 'text/javascript';
    scriptTag.async = async;
    scriptTag.defer = defer;
    scriptTag.onload = expandedOnLoad;
    scriptTag.onerror = expandedOnError;
    scriptTag.src = src;

    document.head.append(scriptTag);
  }
  else {
    document.write(`<script type="${type}" async="${async}" defer="${defer}" onload="${expandedOnLoad}" onerror="${expandedOnError}" src="${src}"></script>`);
  }
}

function requestIdleCallbackFallBack(callback) {
  return setTimeout(function () {
    var start = Date.now();
    callback({
      didTimeout: false,
      timeRemaining: function () {
        return Math.max(0, 50 - (Date.now() - start));
      }
    });
  }, 1);
}

export function requestIdleCallback(callback){
  if(window.requestIdleCallback){
    return window.requestIdleCallback(callback);
  }

  return requestIdleCallbackFallBack(callback);
}



// TODO - move out of this file
export function lazyLoadImage({ el, loadWhen, src, priority }) {
  return new Promise(function(resolve, reject){
    el.classList.add('lazy-loader-initialize');
    var readyToBeginLoading = loadWhen? when(loadWhen): Promise.resolve();
    readyToBeginLoading.then(function () {
      el.classList.remove('lazy-loader-initialize');
      el.classList.add('lazy-loader-loading');

      preloadImage(src, priority || 0).then(function () {
        let loadingAnimationContainerList = el.parentElement.getElementsByClassName('loading-animation-container');
        let loadingAnimationContainer = loadingAnimationContainerList && loadingAnimationContainerList.length && loadingAnimationContainerList[0];
        if (loadingAnimationContainer) {
          loadingAnimationContainer.classList.add('hidden');
        }

        el.classList.remove('lazy-loader-loading');
        el.classList.add('lazy-loader-complete');
        el.src = src;
        el.classList.remove('hidden');

        resolve(el);
      })
        .catch(reject)
    });
  });
}

// TODO - move out of this file
const imageQueue = [];
function preloadImage(src, priority) {
  for(var i = 0; i <= priority; ++i){
    imageQueue[priority] = imageQueue[priority] || [];
  }

  return new Promise(function (resolve, reject) {
    var image = new Image();
    listenTo(image, 'load', resolve);
    listenTo(image, 'error', reject);
    image.src = src;
  });
}

scope(function () {
  function onReadyStateChange() {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      resolvePromiseByName('document.ready', document);
    }

    if (document.readyState === 'complete') {
      resolvePromiseByName('window.load', window);
    }
  }

  listenTo(document, 'readystatechange', onReadyStateChange);
  listenTo(document, 'DOMContentLoaded', onReadyStateChange);
  listenTo(document, 'load', onReadyStateChange);

  onReadyStateChange();
});

window.require = function (name) {
  return window[name];
};

window.evite = window.evite || {};

Object.assign(window.evite, exports);
export default exports;

resolve('evite', exports);



// WEBPACK FOOTER //
// ./assets/es6/common/evite.es6